import React, { useState } from 'react'
import PropTypes from 'prop-types'

/**
 * AddMinorModal Component
 *
 * Modal for adding a minor child to the user's account
 *
 * Props:
 * - isOpen: Whether modal is visible
 * - onClose: Close handler
 * - onAdd: Add handler - receives minor data object
 * - loading: Submit loading state
 */
function AddMinorModal({ isOpen, onClose, onAdd, loading = false }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    relationship_type: 'child'
  })

  const [errors, setErrors] = useState({})

  if (!isOpen) return null

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  /**
   * Validate form
   */
  const validate = () => {
    const newErrors = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle submit
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      await onAdd(formData)
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        relationship_type: 'child'
      })
      setErrors({})
      onClose()
    } catch (err) {
      // Error is handled by the onAdd function
      console.error('Error in AddMinorModal:', err)
    }
  }

  /**
   * Handle close
   */
  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      relationship_type: 'child'
    })
    setErrors({})
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#556B2F] text-white p-6 rounded-t-xl">
          <h2 className="text-2xl font-bold">Add Child to Your Account</h2>
          <p className="text-sm mt-1 opacity-90">
            Children under 18 can access family content through your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* First Name */}
          <div>
            <label className="block text-base font-bold text-gray-700 mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-[#556B2F] ${
                errors.first_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter child's first name"
              disabled={loading}
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-base font-bold text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#556B2F]"
              placeholder="Optional (defaults to your last name)"
              disabled={loading}
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-base font-bold text-gray-700 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#556B2F]"
              disabled={loading}
              max={new Date().toISOString().split('T')[0]}
            />
            <p className="text-sm text-gray-500 mt-1">
              Optional - Used to calculate age
            </p>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-base font-bold text-gray-700 mb-2">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#556B2F]"
              disabled={loading}
            >
              <option value="">Prefer not to say</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="O">Other</option>
            </select>
          </div>

          {/* Relationship Type */}
          <div>
            <label className="block text-base font-bold text-gray-700 mb-2">
              Relationship
            </label>
            <select
              name="relationship_type"
              value={formData.relationship_type}
              onChange={handleChange}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#556B2F]"
              disabled={loading}
            >
              <option value="child">Son/Daughter</option>
              <option value="grandchild">Grandchild</option>
              <option value="nephew">Niece/Nephew</option>
            </select>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Your child will be able to view family stories and content through your account. They won't have their own login until they turn 18.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 text-lg border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 text-lg bg-[#556B2F] text-white rounded-lg hover:bg-[#4A5D23] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

AddMinorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  loading: PropTypes.bool
}

export default AddMinorModal
